/**
 * CLI Attachment Commands
 *
 * List, upload, fetch, and delete org-scoped attachments used by action
 * and notification nodes (config.attachmentIds).
 *
 * @module attachment
 */

import fs from 'node:fs/promises';
import path from 'node:path';
import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';

type AttachmentListItem = {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  createdBy: string | null;
};

type ListResponse = {
  ok: boolean;
  error?: string;
  data: AttachmentListItem[];
  pagination: { page: number; pageSize: number; totalItems: number; totalPages: number };
};

type UploadResponse = {
  ok: boolean;
  error?: string;
  data?: AttachmentListItem[];
};

type UrlResponse = { ok: boolean; error?: string; url?: string };

const VALID_KINDS = ['all', 'image', 'pdf', 'spreadsheet', 'data'] as const;

function printAttachmentHelp(): void {
  console.log(
    [
      'Usage: canary attachment <sub-command> [options]',
      '',
      'Sub-commands:',
      '  list [options]                   List uploaded attachments',
      '  upload <file> [options]          Upload a local file; prints the new ID',
      '  get <id> [--url]                 Print attachment metadata (or signed URL)',
      '  delete <id>                      Soft-delete an attachment',
      '',
      'List options:',
      '  --kind <kind>        Filter: all, image, pdf, spreadsheet, data (default: all)',
      '  --search <query>     Search by filename',
      '  --page <n>           Page number (default: 1)',
      '  --page-size <n>      Page size (default: 25)',
      '  --json               Output raw JSON',
      '',
      'Upload options:',
      '  --filename <name>    Override filename (default: basename of file)',
      '  --json               Output raw JSON',
      '',
      'Get options:',
      '  --url                Print short-lived signed download URL',
      '  --json               Output raw JSON',
      '',
      'Tip: use the returned ID in a node\'s `configJson.attachmentIds` array',
      'for action or notification nodes when creating/patching workflows.',
      '',
      'Common options:',
      '  --env <env>          Target environment (prod, dev, local)',
      '  --api-url <url>      API URL override',
      '  --token <key>        API token override',
    ].join('\n')
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const kind = getArgValue(argv, '--kind');
  const search = getArgValue(argv, '--search');
  const page = getArgValue(argv, '--page');
  const pageSize = getArgValue(argv, '--page-size');
  const jsonOutput = hasFlag(argv, '--json');

  if (kind && !VALID_KINDS.includes(kind as (typeof VALID_KINDS)[number])) {
    console.error(`Error: invalid --kind "${kind}". Must be one of: ${VALID_KINDS.join(', ')}`);
    process.exit(1);
  }

  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (search) params.set('search', search);
  if (page) params.set('page', page);
  if (pageSize) params.set('pageSize', pageSize);

  const qs = params.toString();
  const route = `/org/attachments${qs ? `?${qs}` : ''}`;
  const result = await apiRequest<ListResponse>(apiUrl, token, 'GET', route);

  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify({ data: result.data, pagination: result.pagination }, null, 2));
    return;
  }

  const { data, pagination } = result;
  console.log(
    `Attachments: ${pagination.totalItems} total (page ${pagination.page}/${pagination.totalPages})\n`
  );

  if (data.length === 0) {
    console.log('No attachments found.');
    return;
  }

  const idW = 36;
  const nameW = 40;
  const typeW = 24;
  const sizeW = 10;

  console.log(
    ['ID'.padEnd(idW), 'Filename'.padEnd(nameW), 'Type'.padEnd(typeW), 'Size'.padEnd(sizeW)].join(
      '  '
    )
  );
  for (const a of data) {
    console.log(
      [
        a.id.padEnd(idW),
        truncate(a.filename, nameW).padEnd(nameW),
        truncate(a.contentType, typeW).padEnd(typeW),
        formatSize(a.sizeBytes).padEnd(sizeW),
      ].join('  ')
    );
  }
}

async function handleUpload(argv: string[], apiUrl: string, token: string): Promise<void> {
  const filePath = argv.find((a) => !a.startsWith('-'));
  if (!filePath) {
    console.error('Error: missing file path.');
    console.error('Usage: canary attachment upload <file> [--filename <name>]');
    process.exit(1);
  }

  const overrideName = getArgValue(argv, '--filename');
  const jsonOutput = hasFlag(argv, '--json');

  let buf: Buffer;
  try {
    buf = await fs.readFile(filePath);
  } catch (err) {
    console.error(`Error: failed to read ${filePath}: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  const filename = overrideName ?? path.basename(filePath);
  const form = new FormData();
  form.append('files', new Blob([new Uint8Array(buf)]), filename);

  const res = await fetch(`${apiUrl}/org/attachments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  if (res.status === 401) {
    console.error('Error: Unauthorized. Run: canary login');
    process.exit(1);
  }

  let body: UploadResponse;
  try {
    body = (await res.json()) as UploadResponse;
  } catch {
    console.error(`Error: invalid JSON response (${res.status})`);
    process.exit(1);
  }

  if (!body.ok || !body.data || body.data.length === 0) {
    console.error(`Error: ${body.error ?? `upload failed (${res.status})`}`);
    process.exit(1);
  }

  const [created] = body.data;

  if (jsonOutput) {
    console.log(JSON.stringify(created, null, 2));
    return;
  }

  console.log(`\u2713 Uploaded ${created.filename}`);
  console.log(`  ID: ${created.id}`);
  console.log(`  Type: ${created.contentType}`);
  console.log(`  Size: ${formatSize(created.sizeBytes)}`);
  console.log('');
  console.log('Reference in a workflow node:');
  console.log(`  configJson.attachmentIds = ["${created.id}"]`);
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing attachment ID.');
    console.error('Usage: canary attachment get <id> [--url]');
    process.exit(1);
  }

  const wantUrl = hasFlag(argv, '--url');
  const jsonOutput = hasFlag(argv, '--json');

  if (wantUrl) {
    const result = await apiRequest<UrlResponse>(
      apiUrl,
      token,
      'GET',
      `/org/attachments/${id}/url`
    );
    if (!result.ok || !result.url) {
      console.error(`Error: ${result.error ?? 'failed to fetch signed URL'}`);
      process.exit(1);
    }
    if (jsonOutput) {
      console.log(JSON.stringify({ id, url: result.url }, null, 2));
    } else {
      console.log(result.url);
    }
    return;
  }

  // No dedicated GET by id metadata endpoint; use bulk resolve as the read path.
  const res = await fetch(`${apiUrl}/org/attachments/resolve`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids: [id] }),
  });
  if (res.status === 401) {
    console.error('Error: Unauthorized. Run: canary login');
    process.exit(1);
  }
  const body = (await res.json()) as { ok: boolean; error?: string; data?: AttachmentListItem[] };
  if (!body.ok || !body.data || body.data.length === 0) {
    console.error(`Error: ${body.error ?? 'attachment not found'}`);
    process.exit(1);
  }
  const [att] = body.data;
  if (jsonOutput) {
    console.log(JSON.stringify(att, null, 2));
    return;
  }
  console.log(`${att.filename}`);
  console.log(`  ID: ${att.id}`);
  console.log(`  Type: ${att.contentType}`);
  console.log(`  Size: ${formatSize(att.sizeBytes)}`);
  console.log(`  Created: ${att.createdAt}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: missing attachment ID.');
    console.error('Usage: canary attachment delete <id>');
    process.exit(1);
  }
  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<{ ok: boolean; error?: string }>(
    apiUrl,
    token,
    'DELETE',
    `/org/attachments/${id}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'delete failed'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, id }, null, 2));
    return;
  }
  console.log(`\u2713 Deleted attachment ${id}`);
}

export async function runAttachment(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printAttachmentHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(rest);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      return;
    case 'upload':
      await handleUpload(rest, apiUrl, token);
      return;
    case 'get':
      await handleGet(rest, apiUrl, token);
      return;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      return;
    default:
      console.error(`Unknown attachment sub-command: ${subCommand}`);
      printAttachmentHelp();
      process.exit(1);
  }
}
